import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';

import { MatStepperModule } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

import { AppService } from '@app/app.service';
import { AuthService } from '@app/auth/auth.service';
import { Token } from '@app/auth/token.model';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatTooltipModule,
    MatProgressSpinner,
    ReactiveFormsModule,
    RouterModule,
  ],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordComponent {

  @ViewChild('emailInput') emailInput!: ElementRef<HTMLInputElement>;

  token: Token | null = null;

  hidePassword = true;
  loading = signal(false);

  resendEmailCodeTimerInitial = 30;
  resendEmailCodeInterval: any = null;
  resendEmailCodeTimer = signal(0);

  emailForm: FormGroup;
  verifyTokenForm: FormGroup;
  resetPasswordForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private appService: AppService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    this.verifyTokenForm = this.fb.group({
      email: [{ value: '', disabled: false }, [Validators.required, Validators.email]],
      code: ['', [Validators.required, Validators.minLength(5)]],
    });

    this.resetPasswordForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
    });
  }

  ngOnInit(): void {
    const email = this.route.snapshot.queryParamMap.get('email');
    if (email) {
      this.emailForm.patchValue({ email });
    }
    document.body.classList.add('white-bg');
  }

  checkUserAndSendToken(stepper: any): void {
    if (this.emailForm.invalid) return;

    const email = this.emailForm.value.email;
    this.loading.set(true);

    this.authService.checkUserExistsByEmail(email).subscribe({
      next: () => {
        this.createToken(() => stepper.next());
      },
      error: (err) => {
        this.appService.handleError(err);
        this.loading.set(false);
      },
    });
  }

  createToken(onSuccess?: () => void): void {
    this.loading.set(true);

    this.appService.post('create-token', {
      identifier: this.emailForm.value.email,
      delivery_method: 'email',
    }).subscribe({
      next: (res) => {
        this.token = res.data;

        this.verifyTokenForm.patchValue({
          email: this.emailForm.value.email,
        });

        this.startEmailCodeResendTimer();
        this.loading.set(false);
        onSuccess?.();
      },
      error: (err) => {
        this.appService.handleError(err);
        this.loading.set(false);
      },
    });
  }

  verifyTokenAndContinue(stepper: any): void {
    if (this.verifyTokenForm.invalid || !this.token) return;

    this.loading.set(true);

    this.appService.post('verify-token', {
      code: this.verifyTokenForm.value.code,
      uuid: this.token.uuid,
    }).subscribe({
      next: () => {
        this.loading.set(false);
        stepper.next();
      },
      error: (err) => {
        this.appService.handleError(err);
        this.loading.set(false);
      },
    });
  }

  resetPasswordAndFinish(): void {
    if (this.resetPasswordForm.invalid || !this.token) return;

    const password = this.resetPasswordForm.value.password;
    this.loading.set(true);

    this.authService.changePassword({
      email: this.emailForm.value.email,
      verification_token_uuid: this.token.uuid,
      password,
      password_confirmation: password,
    }).subscribe({
      next: () => {
        this.loading.set(false);
        this.appService.showSnackBar('Password changed successfully', 'success');
        this.router.navigate(['/login']);
      },
      error: (err) => {
        this.appService.handleError(err);
        this.loading.set(false);
      },
    });
  }

  startEmailCodeResendTimer(): void {
    this.resendEmailCodeTimer.set(this.resendEmailCodeTimerInitial);

    this.resendEmailCodeInterval = setInterval(() => {
      if (this.resendEmailCodeTimer() > 0) {
        this.resendEmailCodeTimer.set(this.resendEmailCodeTimer() - 1);
      } else {
        this.clearEmailCodeResendTimer();
      }
    }, 1000);
  }

  clearEmailCodeResendTimer(): void {
    if (this.resendEmailCodeInterval) {
      clearInterval(this.resendEmailCodeInterval);
      this.resendEmailCodeInterval = null;
    }
  }

  goToLogin() {
    if (
      this.emailForm.dirty
    ) {
      this.appService.confirm('Are you sure you want to return to login?').subscribe(result => {
        if (result) this.router.navigate(['/login']);
      });
    } else {
      this.router.navigate(['/login']);
    }
  }

  ngOnDestroy(): void {
    this.clearEmailCodeResendTimer();
    document.body.classList.remove('white-bg');
  }
}